import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import { SideDrawerRef } from '../SideDrawer/SideDrawer.component';
import { useTranslation } from 'react-i18next';
import SideDrawer from '../SideDrawer';
import QAndAContext from '../../providers/QAndA/QAndA.context';
import { Button } from 'ncoded-component-library';
import RadioGroupField from 'components/RadioGroupField';
import QuestionRichTextEditor from '../QuestionRichTextEditor';
import { Form } from 'react-final-form';
import useCallbackRef from 'hooks/useCallbackRef';
import useHeightAnimation from 'hooks/useHeightAnimation';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { isQuestionAnswerApproved } from '../../utils';
import dataRoomApi from '../../dataRoomApi';
import MinusCircleIcon from 'icons/MinusCircle.icon';
import { QuestionPreview } from '../QuestionPreview';
import { QuestionManagingDrawerCommonProps } from '../../models/QAndA';
import PluscircleIcon from 'icons/Pluscircle.icon';
import { FAQAccess } from '../FAQAccess';
import { processFAQAccess } from '../FAQAccess/utils';
import { DATA_ROOM_FAQ_ACCESS, FAQAccessForm } from '../../models/FAQ';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import useGetFollowUpQuestion from '../QAndABuyerContainer/hooks/useGetFollowUpQuestion';
import arrayMutators from 'final-form-arrays';
import useQuestionsPriority from '../../hooks/useQuestionsPriority';

import './PreviewQuestionDrawer.styles.scss';

type PreviewQuestionDrawerProps = QuestionManagingDrawerCommonProps & {
  className?: string;
  hidePrivateInfo?: boolean;
};

export type PreviewSideDrawerRef = SideDrawerRef & {
  showFollowUp?: boolean;
  setShowFollowUp?: (value: boolean) => void;
};

const PreviewQuestionDrawer: React.ForwardRefRenderFunction<
  PreviewSideDrawerRef,
  PreviewQuestionDrawerProps
> = ({ className, question, hidePrivateInfo, canFollowUp, onClose }, ref) => {
  const classes = classNames('preview-question-drawer', className);

  const { t } = useTranslation();

  const { canFaq, faqAccessData, refetch, updateQuestion } =
    useContext(QAndAContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const [newlypromoted, setNewlyPromoted] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [drawer, drawerRef] = useCallbackRef<PreviewSideDrawerRef>();

  const [container, containerRef] = useCallbackRef<HTMLDivElement>();
  const [faqContainer, faqContainerRef] = useCallbackRef<HTMLDivElement>();

  const {
    _id: questionId,
    status,
    isPublishedToFAQ,
    dataRoomParentQuestionId,
    subject,
    otherSubject,
  } = question;

  const { isLoading, displayedQuestions } = useGetFollowUpQuestion(
    question,
    drawer?.open,
  );

  const questionPriorityOptions = useQuestionsPriority();

  useImperativeHandle(
    ref,
    () => ({ ...drawer, showFollowUp, setShowFollowUp }),
    [drawer, showFollowUp],
  );

  useHeightAnimation({
    element: container,
  });

  useHeightAnimation({
    element: faqContainer,
  });

  const showPromote =
    canFaq &&
    !isPublishedToFAQ &&
    !newlypromoted &&
    !dataRoomParentQuestionId &&
    isQuestionAnswerApproved(status);

  const onFollowUpSubmit = useCallback(
    async (values) => {
      try {
        await dataRoomApi.questionsAndAnswers.postFollowUpQuestion(questionId, {
          ...values,
          dataRoomId,
          subject,
          otherSubject,
        });
        popSuccess(t('submitedFollowUpQuestion'));
        refetch();
        drawer?.setOpen(false);
      } catch (e) {
        popServerError(e);
      }
    },
    [
      dataRoomId,
      drawer,
      questionId,
      otherSubject,
      subject,
      popServerError,
      popSuccess,
      refetch,
      t,
    ],
  );

  const onPublishToFaq = useCallback(
    async (values: FAQAccessForm) => {
      try {
        const newValues = processFAQAccess(values, faqAccessData);

        await dataRoomApi.faqs.publishQestionToFaq(questionId, newValues);
        popSuccess(t('publishedToFaq'));
        setNewlyPromoted(true);
      } catch (e) {
        popServerError(e);
      }
    },
    [questionId, faqAccessData, popServerError, popSuccess, t],
  );

  return (
    <SideDrawer
      className={classes}
      title={t('question')}
      ref={drawerRef}
      isLoading={isLoading}
      closeAction={onClose}
    >
      <QuestionPreview
        question={question}
        hidePrivateInfo={hidePrivateInfo}
        displayedQuestions={displayedQuestions}
        updateQuestion={updateQuestion}
      />

      {showPromote && (
        <Form
          mutators={{ ...arrayMutators }}
          initialValues={{ faqAccess: DATA_ROOM_FAQ_ACCESS.ALL }}
          onSubmit={onPublishToFaq}
          render={({ handleSubmit, submitting, invalid }) => {
            return (
              <form
                onSubmit={handleSubmit}
                className="preview-question-drawer__publish"
              >
                <div
                  ref={faqContainerRef}
                  className="preview-question-drawer__animate"
                >
                  <FAQAccess showFAQAccess data={faqAccessData} />
                </div>
                <Button
                  type="submit"
                  style={{ marginTop: '12px' }}
                  disabled={submitting || invalid}
                >
                  {t('promoteThisToFaq')}
                </Button>
              </form>
            );
          }}
        />
      )}

      {canFollowUp && (
        <Button
          variant="link"
          className="preview-question-drawer__follow__trigger"
          onClick={() => setShowFollowUp((old) => !old)}
        >
          {t('askAFollowUp')}
          {showFollowUp ? <MinusCircleIcon /> : <PluscircleIcon />}
        </Button>
      )}

      <div className="preview-question-drawer__animate" ref={containerRef}>
        {canFollowUp && showFollowUp && (
          <Form
            onSubmit={onFollowUpSubmit}
            render={({ handleSubmit, submitting, invalid, pristine }) => {
              return (
                <form
                  onSubmit={handleSubmit}
                  className="preview-question-drawer__follow"
                >
                  <QuestionRichTextEditor label={t('addFollowUpToQuestion')} />

                  <RadioGroupField
                    required
                    options={questionPriorityOptions}
                    name="priority"
                    label={t('priority')}
                    direction="row"
                  />

                  <Button
                    disabled={submitting || invalid || pristine}
                    type="submit"
                    icon={<PluscircleIcon />}
                  >
                    {t('submitFollowUpQuestion')}
                  </Button>
                </form>
              );
            }}
          />
        )}
      </div>
    </SideDrawer>
  );
};

export default forwardRef(PreviewQuestionDrawer);
